import { Alert, Severity, AlertSource } from "../Types/Alerts"
import React, { useState, useEffect, useCallback } from "react"
import { useDisplayOptimality } from "../../../../../Hooks/useDisplayOptimality"
import { useRecoilValue } from "recoil"
import { currentPatientFileInfoAtom } from "../Atoms/PatientFile"
import { MedicationHarmonizer } from "../../../../../Managers/VisualizationManager/ToolBar/Modals/MedicationGraphModalities/MedicationHarmonizer"
import { useModalProvider } from "../../../../../Providers/ModalProvider"
import { MobergColumn, MobergText } from "../../../../../Moberg"

export const useFrontendAlerts = () => {
    const { createModal } = useModalProvider()
    const { isBrowserSupported, isWindowLargeEnough } = useDisplayOptimality()
    const [frontendAlerts, setFrontendAlerts] = useState<Alert[]>([])

    const addAlert = useCallback(({ title, description, resolutions, severity }: Partial<Alert>) => {
        const alertExists = frontendAlerts.some(alert => alert.title === title)
        
        if (alertExists) {
            return
        }

        const newAlert: Alert = {
            id: `${title}-${Date.now()}`,
            deviceId: -1,
            title: title ?? "",
            timestamp: new Date(),
            severity: severity ?? Severity.LOW,
            description: description ?? "",
            resolutions,
            silenced: false,
            source: AlertSource.FRONTEND
        }

        setFrontendAlerts(prev => [...prev, newAlert])
    }, [frontendAlerts])

    const removeAlert = useCallback((title: string) => {
        const alertExists = frontendAlerts.some(alert => alert.title === title)

        if (!alertExists) {
            return
        }

        setFrontendAlerts(prev => prev.filter(alert => alert.title !== title))
    }, [frontendAlerts])


    useEffect(() => {
        const title = "Unsupported Browser"
        
        if (!isBrowserSupported) {
            addAlert({ 
                title, 
                description: "This browser isn't supported and may not work correctly.",
                resolutions: "Please use Google Chrome for the best and most stable experience.",
                severity: Severity.MEDIUM
            })
        } else {
            removeAlert(title)
        }
    }, [addAlert, isBrowserSupported, removeAlert])


    useEffect(() => {
        const title = "Window Too Small"
        
        if (!isWindowLargeEnough) {
            addAlert({ 
                title, 
                description: "Some content may not display correctly.",
                resolutions: "Increase the size of the current window or zoom out",
                severity: Severity.LOW
            })
        } else {
            removeAlert(title)
        }
    }, [isWindowLargeEnough, frontendAlerts, addAlert, removeAlert])

    
    const { patientMedications } = useRecoilValue(currentPatientFileInfoAtom)

    useEffect(() => {
        const title = "Unrecognized medication(s)"
        
        if (patientMedications?.unharmonized?.length > 0) {
            addAlert({ 
                title, 
                description: `These medications will not be shown in the medications display.`,
                resolutions: (
                    <MobergColumn>
                        <MobergText>Choose the correct name for each unrecognized medication:</MobergText>
                        <MedicationHarmonizer />
                    </MobergColumn>
                ),
                severity: Severity.MEDIUM
            })
        } else {
            removeAlert(title)
        }
    }, [patientMedications, addAlert, createModal, removeAlert])

    return { frontendAlerts, setFrontendAlerts }
}